/**
 * Client for the Network Restore / Snapshot REST endpoints.
 *
 *   GET  /networks/{id}/snapshots                  → unified baseline +
 *                                                    releases + checkpoints
 *   GET  /networks/{id}/restore-preview            → dry-run diff counts
 *   POST /networks/{id}/restore                    → auto-checkpoint + apply
 *
 * Kept deliberately thin: all UI state (confirm-gate, mode selection, diff
 * formatting) lives in ``src/utils/snapshotList.js`` +
 * ``src/utils/restoreDialog.js`` so the React components stay declarative.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// ``USER_SNAPSHOT`` is surfaced by the backend but shares the
// ``NetworkCheckpoint`` storage with ``CHECKPOINT``. The UI treats them
// differently (own row action menu, own "Save snapshot" button), but for
// restore purposes both resolve to a ``sourceKind=CHECKPOINT`` plus a
// ``sourceCheckpointAt`` pointer — see ``toRestoreSource`` in
// ``utils/snapshotList.js`` for the mapping.
export type SnapshotKind = 'BASELINE' | 'RELEASE' | 'CHECKPOINT' | 'USER_SNAPSHOT';
export type RestorableSnapshotKind = Exclude<SnapshotKind, 'USER_SNAPSHOT'>;

export type BaselineSource = 'BRAIN_DEPLOY' | 'DRIFT_PROMOTE';

export type ReleaseStatus = 'ACTIVE_RELEASE' | 'DEACTIVATED';

export interface SnapshotSummary {
  agentCount: number;
  agentNames: string[];
  logLevel?: string | null;
  networkHumanReviewMode?: string | null;
  vectorDbProvider?: string | null;
}

// PR 4: graph-fingerprint pair surfaced by the /snapshots list so the UI
// can label each row "config only" (graph matches baseline) vs
// "graph + config" (graph differs). ``matchesBaselineGraph`` is null when
// either the row or the baseline has no rich ASL — the UI hides the chip
// in that case rather than guessing.
export interface SnapshotGraphLabel {
  graphFingerprint?: string | null;
  matchesBaselineGraph?: boolean | null;
  /** False when the DDB row has no S3 manifest (export/claim-check unavailable). */
  manifestReady?: boolean;
  /**
   * Section 2 of the version+snapshot rework. Tags every snapshot with
   * the network Version it was taken under:
   *   * ``"InProgress"`` — taken while the network was unreleased /
   *     live-editable. Cannot be applied to a Released network.
   *   * ``"v1"`` / ``"v2"`` / … — taken under that immutable Version.
   *     Can only be applied to a room running the same Version.
   * Legacy rows (pre-Section-2) coerce to ``"InProgress"`` server-side.
   */
  parentVersion?: string;
}

export interface BaselineEntry extends SnapshotGraphLabel {
  kind: 'BASELINE';
  networkId: string;
  capturedAt: string;
  capturedBy: string;
  source: BaselineSource;
  summary: SnapshotSummary;
  snapshot?: Record<string, unknown>;
}

export interface ReleaseEntry extends SnapshotGraphLabel {
  kind: 'RELEASE';
  networkId: string;
  releaseVersion: number;
  releasedAt: string;
  releasedBy: string;
  tenantId: string;
  supervisorSubs: string[];
  // Lifecycle fields — absent on rows returned from the legacy
  // /releases/history endpoint (see _serialize_network_release), present
  // on rows returned from /snapshots (see
  // _serialize_network_release_with_status in the backend).
  releaseStatus?: ReleaseStatus;
  deactivatedAt?: string;
  deactivatedBy?: string;
  deleteEligible?: boolean;
  // Pools currently pinned to this release_version, surfaced in /snapshots
  // so the Deactivate dialog can render impact inline without a second
  // round-trip. Empty list = release is safe to deactivate/delete from a
  // pool-pinning standpoint.
  pinnedPools?: ImpactedPool[];
  pinnedPoolCount?: number;
  summary?: SnapshotSummary;
  snapshot?: Record<string, unknown>;
}

export interface CheckpointEntry extends SnapshotGraphLabel {
  kind: 'CHECKPOINT';
  networkId: string;
  capturedAt: string;
  capturedBy: string;
  sourceKind: RestorableSnapshotKind | 'LIVE';
  sourceVersion: number | null;
  sourceCheckpointAt: string | null;
  reason: string;
  summary: SnapshotSummary;
  snapshot?: Record<string, unknown>;
}

export interface UserSnapshotEntry extends SnapshotGraphLabel {
  kind: 'USER_SNAPSHOT';
  networkId: string;
  capturedAt: string;
  capturedBy: string;
  sourceKind: 'LIVE' | RestorableSnapshotKind;
  sourceVersion: number | null;
  sourceCheckpointAt: string | null;
  reason: 'user-snapshot';
  label?: string;
  contentHash?: string;
  summary: SnapshotSummary;
  snapshot?: Record<string, unknown>;
}

export type SnapshotEntry =
  | BaselineEntry
  | ReleaseEntry
  | CheckpointEntry
  | UserSnapshotEntry;

export interface ImpactedPool {
  poolId: string;
  name: string;
}

export interface ListSnapshotsResponse {
  networkId: string;
  snapshots: SnapshotEntry[];
  count: number;
  baselineSource: BaselineSource | null;
  checkpointCapacity: number;
  userSnapshotCapacity?: number;
  deactivationGraceDays?: number;
}

export interface RestoreSource {
  sourceKind: SnapshotKind;
  sourceVersion?: number | null;
  sourceCheckpointAt?: string | null;
}

export interface RestorePreviewResponse {
  networkId: string;
  source: RestoreSource & { kind: SnapshotKind; capturedAt?: string };
  summary: SnapshotSummary;
  counts: {
    add: number;
    update: number;
    unchanged: number;
    delete: number;
    networkFieldChanges: number;
  };
  agents: {
    add: Array<{ agentId?: string; name?: string }>;
    update: Array<{ agentId?: string; name?: string }>;
    delete: string[];
    unchanged: string[];
  };
  networkFieldChanges: Record<string, { from: unknown; to: unknown }>;
  networkStatus: string;
  // PR 4: true iff the snapshot's ``interface.asl`` differs from live.
  // The UI reads this to conditionally show the
  // "restore config only (keep current graph)" checkbox in the confirm
  // dialog — when the graph is unchanged there's nothing to toggle.
  graphChanged?: boolean;
  liveGraphFingerprint?: string | null;
  snapshotGraphFingerprint?: string | null;
}

export interface RestoreResponse {
  networkId: string;
  source: RestoreSource & { kind: SnapshotKind; capturedAt?: string };
  counts: RestorePreviewResponse['counts'];
  checkpoint: {
    capturedAt: string;
    sourceKind: SnapshotKind;
    sourceVersion: number | null;
    sourceCheckpointAt: string | null;
  };
  networkStatus: string;
  pendingReleaseChanges: boolean;
  // PR 3: server-emitted operator-facing notices the UI surfaces via the
  // shell's notify channel. Always at least one entry (the "knowledge
  // base not restored" reminder).
  warnings?: string[];
  // PR 3: true iff restore actually rotated the live ``nw-{id}`` Step
  // Functions definition (snapshot graph differed from live).
  aslRedeployed?: boolean;
  aslRedeployError?: string | null;
  snapshotAslHash?: string | null;
  liveAslHash?: string | null;
  // PR 4: echoed back from the server when the caller passed
  // ``configOnly: true``. The UI uses this to adjust the success toast
  // ("restored agents only — graph unchanged") and to re-render the
  // dialog copy next time the user opens it on the same snapshot.
  configOnly?: boolean;
}

// PR 3: 409 payload shape returned when the active-release graph-change
// gate refuses the restore. The dialog reads ``blockReason`` to decide
// whether to offer the override path; ``activeReleaseCount`` is shown
// inline so the user understands what's at risk.
export interface RestoreActiveReleaseGraphBlock {
  error: string;
  blockReason: 'ACTIVE_RELEASE_GRAPH_CHANGE';
  activeReleaseCount: number;
  snapshotAslHash?: string | null;
  liveAslHash?: string | null;
  message?: string;
}

function requireToken(): string {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

function baseUrl(): string {
  return API_BASE_URL.replace(/\/$/, '');
}

/**
 * Pure helper: turn a RestoreSource + optional confirm string into the
 * POST /restore request body. Exposed so the UI's pure ``restoreDialog.js``
 * helpers (and our Vitest suite) can exercise the shaping without spinning
 * up fetch mocks.
 */
export function buildRestoreRequestBody(
  source: RestoreSource,
  opts: {
    confirm: string;
    allowReleaseGraphChange?: boolean;
    // PR 4: when true the server skips the Step Functions update even if
    // the snapshot's graph differs from live. Agent/field changes still
    // apply. The UI only ever sets this on the dedicated "config only"
    // checkbox in ``RestoreConfirmDialog`` (visible when preview reports
    // graph drift) — never by default.
    configOnly?: boolean;
  },
): Record<string, unknown> {
  if (!source || typeof source !== 'object') {
    throw new Error('restore source is required');
  }
  const kind = String(source.sourceKind || '').toUpperCase();
  if (kind !== 'BASELINE' && kind !== 'RELEASE' && kind !== 'CHECKPOINT') {
    throw new Error(`Unsupported sourceKind: ${source.sourceKind}`);
  }
  const confirm = (opts?.confirm || '').trim();
  if (!confirm) {
    throw new Error('confirm (network name) is required');
  }
  const body: Record<string, unknown> = {
    sourceKind: kind,
    confirm,
  };
  if (kind === 'RELEASE') {
    const v = Number(source.sourceVersion);
    if (!Number.isFinite(v) || v < 1) {
      throw new Error('sourceVersion must be a positive integer for RELEASE');
    }
    body.sourceVersion = Math.floor(v);
  }
  if (kind === 'CHECKPOINT') {
    const at = (source.sourceCheckpointAt || '').trim();
    if (!at) {
      throw new Error('sourceCheckpointAt is required for CHECKPOINT');
    }
    body.sourceCheckpointAt = at;
  }
  // PR 3: forwarded only when explicitly true so the server's default
  // (refuse graph changes on networks with active releases) is preserved
  // when the caller doesn't pass the flag.
  if (opts?.allowReleaseGraphChange === true) {
    body.allowReleaseGraphChange = true;
  }
  // PR 4: same shape for ``configOnly`` — only set on explicit opt-in.
  if (opts?.configOnly === true) {
    body.configOnly = true;
  }
  return body;
}

/**
 * Pure helper: turn a RestoreSource into the query string for the preview
 * endpoint. Mirrors ``buildRestoreRequestBody`` so the two paths agree on
 * how sources are encoded.
 */
export function buildRestorePreviewQuery(source: RestoreSource): string {
  if (!source || typeof source !== 'object') {
    throw new Error('restore source is required');
  }
  const kind = String(source.sourceKind || '').toUpperCase();
  if (kind !== 'BASELINE' && kind !== 'RELEASE' && kind !== 'CHECKPOINT') {
    throw new Error(`Unsupported sourceKind: ${source.sourceKind}`);
  }
  const params = new URLSearchParams();
  params.set('sourceKind', kind);
  if (kind === 'RELEASE') {
    const v = Number(source.sourceVersion);
    if (!Number.isFinite(v) || v < 1) {
      throw new Error('sourceVersion must be a positive integer for RELEASE');
    }
    params.set('sourceVersion', String(Math.floor(v)));
  }
  if (kind === 'CHECKPOINT') {
    const at = (source.sourceCheckpointAt || '').trim();
    if (!at) {
      throw new Error('sourceCheckpointAt is required for CHECKPOINT');
    }
    params.set('sourceCheckpointAt', at);
  }
  return params.toString();
}

export async function listNetworkSnapshots(
  networkId: string,
  opts?: { includeSnapshot?: boolean },
): Promise<ListSnapshotsResponse> {
  const token = requireToken();
  const params = new URLSearchParams();
  if (opts?.includeSnapshot) params.set('includeSnapshot', 'true');
  const q = params.toString();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/snapshots${q ? `?${q}` : ''}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = (await res.json()) as Partial<ListSnapshotsResponse>;
  return {
    networkId: data.networkId ?? networkId,
    snapshots: Array.isArray(data.snapshots) ? data.snapshots : [],
    count: typeof data.count === 'number' ? data.count : (data.snapshots?.length ?? 0),
    baselineSource: (data.baselineSource ?? null) as BaselineSource | null,
    checkpointCapacity:
      typeof data.checkpointCapacity === 'number' ? data.checkpointCapacity : 10,
  };
}

export async function getRestorePreview(
  networkId: string,
  source: RestoreSource,
): Promise<RestorePreviewResponse> {
  const token = requireToken();
  const qs = buildRestorePreviewQuery(source);
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/restore-preview?${qs}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as RestorePreviewResponse;
}

/**
 * Thrown by ``restoreNetwork`` when the server returned 409 with the
 * ``ACTIVE_RELEASE_GRAPH_CHANGE`` block. The dialog catches this
 * specifically so it can render the "are you sure" override path
 * instead of a generic toast — every other failure surfaces as a
 * plain ``Error``.
 */
export class RestoreActiveReleaseGraphBlockError extends Error {
  block: RestoreActiveReleaseGraphBlock;
  constructor(block: RestoreActiveReleaseGraphBlock) {
    super(block.error || 'Restore would change the deployed graph.');
    this.name = 'RestoreActiveReleaseGraphBlockError';
    this.block = block;
  }
}

export async function restoreNetwork(
  networkId: string,
  source: RestoreSource,
  opts: {
    confirm: string;
    allowReleaseGraphChange?: boolean;
    configOnly?: boolean;
  },
): Promise<RestoreResponse> {
  const token = requireToken();
  const body = buildRestoreRequestBody(source, opts);
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/restore`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    if (res.status === 409 && t) {
      try {
        const parsed = JSON.parse(t);
        if (parsed && parsed.blockReason === 'ACTIVE_RELEASE_GRAPH_CHANGE') {
          throw new RestoreActiveReleaseGraphBlockError(
            parsed as RestoreActiveReleaseGraphBlock,
          );
        }
      } catch (e) {
        if (e instanceof RestoreActiveReleaseGraphBlockError) throw e;
        // fall through to the generic error
      }
    }
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as RestoreResponse;
}

// ---------------------------------------------------------------------------
// Snapshot lifecycle — save / rename / delete (user-initiated)
// ---------------------------------------------------------------------------

export interface SaveSnapshotResponse {
  networkId: string;
  snapshot: UserSnapshotEntry;
  deduped: boolean;
}

export interface DeleteSnapshotResponse {
  networkId: string;
  capturedAt: string;
  deleted: boolean;
  alreadyAbsent?: boolean;
}

export interface CapturePreRunSnapshotResponse {
  networkId: string;
  captured: boolean;
  reason?: string;
  snapshot?: CheckpointEntry;
}

/**
 * Fire-and-forget auto-snapshot used by the Run tab before every
 * execution. Dedupes by content-hash on the server, so a burst of runs
 * against unchanged state produces at most one row. Callers should treat
 * this as best-effort — a failure here must never block the run.
 */
export async function capturePreRunSnapshot(
  networkId: string,
): Promise<CapturePreRunSnapshotResponse> {
  const token = requireToken();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/snapshots/pre-run`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as CapturePreRunSnapshotResponse;
}

export async function saveUserSnapshot(
  networkId: string,
  opts?: { label?: string | null },
): Promise<SaveSnapshotResponse> {
  const token = requireToken();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/snapshots`;
  // Trim label client-side so we don't round-trip whitespace-only strings.
  const label = typeof opts?.label === 'string' ? opts.label.trim() : null;
  const body = label ? { label } : {};
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as SaveSnapshotResponse;
}

export async function renameUserSnapshot(
  networkId: string,
  capturedAt: string,
  label: string | null,
): Promise<UserSnapshotEntry> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}` +
    `/snapshots/${encodeURIComponent(capturedAt)}`;
  const trimmed = typeof label === 'string' ? label.trim() : null;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ label: trimmed }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.snapshot as UserSnapshotEntry;
}

export async function deleteUserSnapshot(
  networkId: string,
  capturedAt: string,
): Promise<DeleteSnapshotResponse> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}` +
    `/snapshots/${encodeURIComponent(capturedAt)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as DeleteSnapshotResponse;
}

// ---------------------------------------------------------------------------
// Release lifecycle — deactivate / reactivate / delete
// ---------------------------------------------------------------------------

export interface DeactivateReleaseResponse {
  networkId: string;
  releaseVersion: number;
  deactivatedAt?: string;
  deactivatedBy?: string;
  alreadyDeactivated?: boolean;
  impactedPools: ImpactedPool[];
}

export interface ReactivateReleaseResponse {
  networkId: string;
  releaseVersion: number;
  releaseStatus: ReleaseStatus;
}

export interface DeleteReleaseResponse {
  networkId: string;
  releaseVersion: number;
  deleted: boolean;
}

// Publish-to-registry (NETWORK-REGISTRY.md). The backend builds a portable
// package (manifest + catalog + context + skills) from the network's release and
// writes a DRAFT row + zip to the shared-services registry. The creator may
// override the Haiku-suggested catalog doc (title/summary/domain/tags).
export interface PublishToRegistryCatalog {
  title: string;
  summary: string;
  domain: string;
  tags: string[];
  agentCount: number;
  generatedBy?: string;
}

export interface PublishToRegistryResponse {
  registryNetworkId: string;
  version: number;
  /** The network's semantic version label (e.g. "1.00") — the display/upgrade key. */
  stepflowVersion: string;
  status: 'DRAFT' | 'RELEASED';
  catalog: PublishToRegistryCatalog;
  skillCount: number;
  sourceReleaseVersion: number;
  sizeBytes: number;
}

export interface PublishToRegistryBody {
  version?: number;      // release version to publish (defaults to latest active)
  title?: string;
  summary?: string;
  domain?: string;
  tags?: string[];
}

export async function publishNetworkToRegistry(
  networkId: string,
  body: PublishToRegistryBody = {},
): Promise<PublishToRegistryResponse> {
  const token = requireToken();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/publish`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as PublishToRegistryResponse;
}

/** GET the architect-authored catalog defaults ({title, summary, domain, tags})
 *  so the Publish-to-Registry dialog can PREFILL all four fields. Read-only. */
export async function getRegistryCatalogPreview(
  networkId: string,
  version?: number,
): Promise<{ title: string; summary: string; domain: string; tags: string[] }> {
  const token = requireToken();
  const q = version && version >= 1 ? `?version=${encodeURIComponent(String(version))}` : '';
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/publish${q}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const d = (await res.json()) as Record<string, unknown>;
  return {
    title: String(d.title || ''),
    summary: String(d.summary || ''),
    domain: String(d.domain || ''),
    tags: Array.isArray(d.tags) ? (d.tags as unknown[]).map((t) => String(t)) : [],
  };
}

export async function deactivateNetworkRelease(
  networkId: string,
  releaseVersion: number,
): Promise<DeactivateReleaseResponse> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}` +
    `/releases/${encodeURIComponent(String(releaseVersion))}/deactivate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as DeactivateReleaseResponse;
}

export async function reactivateNetworkRelease(
  networkId: string,
  releaseVersion: number,
): Promise<ReactivateReleaseResponse> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}` +
    `/releases/${encodeURIComponent(String(releaseVersion))}/reactivate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as ReactivateReleaseResponse;
}

export async function deleteNetworkRelease(
  networkId: string,
  releaseVersion: number,
): Promise<DeleteReleaseResponse> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}` +
    `/releases/${encodeURIComponent(String(releaseVersion))}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as DeleteReleaseResponse;
}

// ---------------------------------------------------------------------------
// Network deletion — impact preview + gated DELETE
// ---------------------------------------------------------------------------
//
// The destructive ``DELETE /networks/{id}`` endpoint enforces two gates
// server-side (active-release block + type-the-name confirmation). The UI
// fetches ``/deletion-impact`` first to render either the blocked dialog
// (active releases exist) or the deletable dialog (type-to-confirm form).
// The server is the source of truth — the UI gates are only there to keep
// users from racing destructive POSTs.

export type NetworkDeletionBlockReason =
  | 'NETWORK_HAS_ACTIVE_RELEASES'
  | string;

export interface DeletionImpactReleaseEntry {
  releaseVersion: number;
  releasedAt?: string | null;
  releasedBy?: string | null;
  pinnedPools: ImpactedPool[];
  pinnedPoolCount: number;
  deactivatedAt?: string | null;
  deactivatedBy?: string | null;
}

export interface DeletionImpactSnapshotEntry {
  capturedAt: string;
  capturedBy?: string | null;
  label: string;
}

export interface NetworkDeletionImpact {
  networkId: string;
  networkName: string;
  userId?: string;
  activeReleases: DeletionImpactReleaseEntry[];
  activeReleaseCount: number;
  deactivatedReleases: DeletionImpactReleaseEntry[];
  deactivatedReleaseCount: number;
  userSnapshots: DeletionImpactSnapshotEntry[];
  userSnapshotCount: number;
  checkpointCount: number;
  baselinePresent: boolean;
  canDelete: boolean;
  blockReason: NetworkDeletionBlockReason | null;
  /** THE TRASH (LIFECYCLE.md §3b) — the workspace's resolved deletion policy. */
  deletionPolicy?: { allowDirectDelete: boolean; trashDays: number; deleteForever: boolean; roomsMustBeArchived: boolean };
  /** Rooms that block a delete in a protected workspace (not ARCHIVED). */
  liveRooms?: { poolId: string; name: string; status: string }[];
  /** How many rooms go with the network. */
  roomCount?: number;
}

export interface DeleteNetworkResponse {
  message: string;
  networkId: string;
  rowsDeleted?: Record<string, number>;
  /** THE TRASH (LIFECYCLE.md §3b): true = it waits in the Trash until ``purgeAt``; false = removed right away. */
  trashed?: boolean;
  purgeAt?: string;
  purge?: string;
  policy?: { allowDirectDelete: boolean; trashDays: number };
}

/** A failed call carries the server's ``{error, message}`` (``err.status`` / ``err.data``); ``err.message`` stays the
 *  raw body (callers match on the error code in it). */
async function _failed(res: Response): Promise<never> {
  const t = await res.text();
  const err: Error & { status?: number; data?: any } = new Error(t || `HTTP ${res.status}`);
  err.status = res.status;
  try { err.data = JSON.parse(t); } catch { err.data = {}; }
  throw err;
}

/**
 * Read-only impact preview for ``DELETE /networks/{id}``. Drives
 * ``DeleteNetworkDialog``: when ``canDelete === false`` the dialog renders
 * the constructive blocked state ("deactivate first → here are the
 * deployments"); otherwise it renders the type-to-confirm form.
 */
export async function getNetworkDeletionImpact(
  networkId: string,
): Promise<NetworkDeletionImpact> {
  const token = requireToken();
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}/deletion-impact`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = (await res.json()) as Partial<NetworkDeletionImpact>;
  // Defensive normalisation — ``canDelete`` and the array fields drive
  // dialog rendering; never let a malformed response leave them undefined.
  return {
    networkId: data.networkId ?? networkId,
    networkName: data.networkName ?? '',
    userId: data.userId,
    activeReleases: Array.isArray(data.activeReleases) ? data.activeReleases : [],
    activeReleaseCount:
      typeof data.activeReleaseCount === 'number'
        ? data.activeReleaseCount
        : (data.activeReleases?.length ?? 0),
    deactivatedReleases: Array.isArray(data.deactivatedReleases)
      ? data.deactivatedReleases
      : [],
    deactivatedReleaseCount:
      typeof data.deactivatedReleaseCount === 'number'
        ? data.deactivatedReleaseCount
        : (data.deactivatedReleases?.length ?? 0),
    userSnapshots: Array.isArray(data.userSnapshots) ? data.userSnapshots : [],
    userSnapshotCount:
      typeof data.userSnapshotCount === 'number'
        ? data.userSnapshotCount
        : (data.userSnapshots?.length ?? 0),
    checkpointCount:
      typeof data.checkpointCount === 'number' ? data.checkpointCount : 0,
    baselinePresent: !!data.baselinePresent,
    canDelete: data.canDelete === true,
    blockReason: (data.blockReason ?? null) as NetworkDeletionBlockReason | null,
    // THE TRASH (LIFECYCLE.md §3b) — what the delete WILL do + the rooms it takes (this normaliser dropped them, so the
    // dialog showed the old "permanently" copy in a workspace with a Trash window)
    ...(data.deletionPolicy ? { deletionPolicy: data.deletionPolicy } : {}),
    liveRooms: Array.isArray(data.liveRooms) ? data.liveRooms : [],
    roomCount: typeof data.roomCount === 'number' ? data.roomCount : 0,
  };
}

/**
 * Pure helper: build the ``?confirm=`` query string for the gated
 * DELETE call. Exposed so unit tests can lock the encoding without
 * spinning up fetch mocks.
 */
export function buildDeleteNetworkQuery(opts: { confirm: string }): string {
  const confirm = (opts?.confirm || '').trim();
  if (!confirm) {
    throw new Error('confirm (network name) is required');
  }
  const params = new URLSearchParams();
  params.set('confirm', confirm);
  return params.toString();
}

export async function deleteNetwork(
  networkId: string,
  opts: { confirm: string; forever?: boolean },
): Promise<DeleteNetworkResponse> {
  const token = requireToken();
  const qs = buildDeleteNetworkQuery(opts) + (opts?.forever ? '&forever=1' : '');
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}?${qs}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) return _failed(res);
  return (await res.json()) as DeleteNetworkResponse;
}

export interface TrashedNetwork { networkId: string; name: string; trashedAt: string; purgeAt: string; trashedBy: string }

/** THE TRASH (LIFECYCLE.md §3b) — what this workspace deleted + its policy (``deleteForever`` allowed?). */
export async function listTrashedNetworks(): Promise<{ networks: TrashedNetwork[]; policy: any }> {
  const res = await fetch(`${baseUrl()}/networks?trash=1`, {
    headers: { Authorization: `Bearer ${requireToken()}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) return _failed(res);
  return res.json();
}

/** Undo a delete — the network and its rooms come back exactly as they were. */
export async function untrashNetwork(networkId: string): Promise<{ restored: boolean }> {
  const res = await fetch(`${baseUrl()}/networks/${encodeURIComponent(networkId)}/untrash`, {
    method: 'POST', headers: { Authorization: `Bearer ${requireToken()}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) return _failed(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// PR 4 — Snapshot export / network import (marketplace foundation)
// ---------------------------------------------------------------------------
//
// Export downloads the same ``NetworkManifest`` JSON the backend persists
// to S3 (claim-check) with the per-account metadata sanitized, so the
// file is safe to commit to VCS, share with another tenant, or re-import.
// Import is the inverse — POST a manifest and get a fresh DRAFT network
// hydrated in the caller's tenant.

/**
 * Pure helper: build the ``?sourceKind=...`` query string for the export
 * endpoint. Mirrors ``buildRestoreRequestBody`` so the three paths
 * (preview, restore, export) agree on how snapshot sources are encoded.
 */
export function buildSnapshotExportQuery(source: RestoreSource): string {
  if (!source || typeof source !== 'object') {
    throw new Error('export source is required');
  }
  const kind = String(source.sourceKind || '').toUpperCase();
  if (kind !== 'BASELINE' && kind !== 'RELEASE' && kind !== 'CHECKPOINT') {
    throw new Error(`Unsupported sourceKind for export: ${source.sourceKind}`);
  }
  const params = new URLSearchParams();
  params.set('sourceKind', kind);
  if (kind === 'RELEASE') {
    const v = Number(source.sourceVersion);
    if (!Number.isFinite(v) || v < 1) {
      throw new Error('sourceVersion must be a positive integer for RELEASE');
    }
    params.set('sourceVersion', String(Math.floor(v)));
  }
  if (kind === 'CHECKPOINT') {
    const at = (source.sourceCheckpointAt || '').trim();
    if (!at) {
      throw new Error('sourceCheckpointAt is required for CHECKPOINT');
    }
    params.set('sourceCheckpointAt', at);
  }
  return params.toString();
}

export interface ExportSnapshotResult {
  /**
   * Raw ``.zip`` archive bytes as returned by the server. The archive
   * contains ``manifest.json`` plus any future sibling artifacts
   * (``bible.json`` / ``chat_history.json`` / per-agent files).
   * Callers that just want to trigger a browser download should use
   * ``downloadSnapshotManifest``; callers that need to inspect the
   * manifest JSON client-side can unzip this blob with any ZIP reader
   * (JSZip, native ``DecompressionStream`` in modern browsers, …).
   */
  archive: Blob;
  filename: string;
}

/**
 * Fetch a portable snapshot archive (``.zip``) for a snapshot. Returns
 * the raw archive Blob along with the server-suggested filename (picked
 * up from the ``Content-Disposition`` header). Callers typically wrap
 * this with ``downloadSnapshotManifest`` which actually triggers the
 * browser save; this function is exported separately so UI tests can
 * verify the HTTP contract without touching DOM APIs.
 *
 * Server contract: ``Content-Type: application/zip``, body is a
 * binary ZIP. API Gateway must list ``application/zip`` in
 * ``binary_media_types`` — otherwise the response body arrives as a
 * base64 string and the browser saves a corrupt archive.
 */
export async function exportSnapshotManifest(
  networkId: string,
  source: RestoreSource,
): Promise<ExportSnapshotResult> {
  const token = requireToken();
  const qs = buildSnapshotExportQuery(source);
  const url =
    `${baseUrl()}/networks/${encodeURIComponent(networkId)}/snapshots/export?${qs}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/zip',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const archive = await res.blob();
  const dispo = res.headers.get('content-disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(dispo);
  const filename = (match && match[1]) || `${networkId}-snapshot.stepflow.zip`;
  return { archive, filename };
}

/**
 * Convenience wrapper: fetch the archive and trigger a browser download.
 * Kept separate from ``exportSnapshotManifest`` so the pure HTTP path
 * stays testable without mocking ``URL.createObjectURL``.
 */
export async function downloadSnapshotManifest(
  networkId: string,
  source: RestoreSource,
): Promise<{ filename: string }> {
  const { archive, filename } = await exportSnapshotManifest(networkId, source);
  const objectUrl = URL.createObjectURL(archive);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Release the object URL on the next tick; some browsers queue the
    // download slightly after the click handler returns.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
  return { filename };
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

export interface ImportNetworkResponse {
  network: Record<string, unknown>;
  imported: {
    manifestVersion: number;
    sourceTenantId?: string | null;
    sourceNetworkId?: string | null;
    agentsImported: number;
    aslImported: boolean;
  };
  nextStep?: string;
}

/**
 * Base64-encode a ``Blob`` / ``File`` / ``ArrayBuffer`` for transport
 * inside a JSON body. We deliberately avoid ``FileReader.readAsDataURL``
 * so callers can pass any binary source; ``btoa`` on a chunked binary
 * string is safe for the multi-MB range a StepFlow archive falls in.
 */
async function blobOrBufferToBase64(
  input: Blob | ArrayBuffer | Uint8Array,
): Promise<string> {
  let bytes: Uint8Array;
  if (input instanceof Uint8Array) {
    bytes = input;
  } else if (input instanceof ArrayBuffer) {
    bytes = new Uint8Array(input);
  } else if (typeof (input as Blob).arrayBuffer === 'function') {
    bytes = new Uint8Array(await (input as Blob).arrayBuffer());
  } else {
    // Fallback for environments whose ``Blob`` shim doesn't
    // implement ``arrayBuffer()`` (notably older jsdom). ``FileReader``
    // is ubiquitous and works on every browser StepFlow supports.
    const buf = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error || new Error('FileReader error'));
      reader.readAsArrayBuffer(input as Blob);
    });
    bytes = new Uint8Array(buf);
  }
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

/**
 * Create a new network in the caller's tenant from an exported
 * snapshot. Two shapes are accepted:
 *
 *   * ``archive`` — a ``Blob`` / ``File`` / ``ArrayBuffer`` /
 *     ``Uint8Array`` holding a ``.zip`` produced by
 *     ``exportSnapshotManifest``. Forward-compatible with future
 *     multi-file exports (``bible.json``, ``chat_history.json``,
 *     per-agent files inside the archive).
 *   * ``manifest`` — legacy inline JSON object (the unzipped
 *     ``manifest.json``). Supported for automation that already
 *     parses the file client-side.
 *
 * Any override fields (``name``, ``networkId``) win over the
 * manifest's own values so users can rename as they import.
 */
export async function importNetworkFromManifest(opts: {
  archive?: Blob | ArrayBuffer | Uint8Array;
  manifest?: Record<string, unknown>;
  name?: string;
  networkId?: string;
}): Promise<ImportNetworkResponse> {
  if (!opts || (!opts.archive && !opts.manifest)) {
    throw new Error('archive or manifest is required');
  }
  const token = requireToken();
  const url = `${baseUrl()}/networks/import`;
  const body: Record<string, unknown> = {};
  if (opts.archive) {
    body.archive = await blobOrBufferToBase64(opts.archive);
  } else if (opts.manifest && typeof opts.manifest === 'object') {
    body.manifest = opts.manifest;
  } else {
    throw new Error('archive or manifest is required');
  }
  if (opts.name && opts.name.trim()) body.name = opts.name.trim();
  if (opts.networkId && opts.networkId.trim()) body.networkId = opts.networkId.trim();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as ImportNetworkResponse;
}
